import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { ITEMS, ITEM_CATEGORIES, type ItemCategory } from "@/lib/items-data";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

const TITLE = "Palworld Items & Weapons Database";
const DESC =
  "Every Palworld item, weapon, armour, Pal Sphere, structure and material with its unlock level, crafting recipe and best use — one page per item.";
const URL = "https://palstars.com/items";

const FAQ = [
  {
    q: "How many items are in the Palworld item database?",
    a: `Pal Stars indexes ${ITEMS.length} Palworld items across weapons, ammunition, Pal Spheres, armour, tools, structures, consumables and materials, each with its own page listing the unlock level, recipe and best use.`,
  },
  {
    q: "What is the best weapon in Palworld?",
    a: "The Legendary Assault Rifle has the highest sustained damage in Palworld, unlocked with Ancient Technology Points at level 46. Before that the standard Assault Rifle at level 24 is the strongest option.",
  },
  {
    q: "What is the best Pal Sphere in Palworld?",
    a: "The Legendary Sphere has the highest catch rate and is required for reliable legendary captures. Ultra Spheres are the best value for everyday catching above level 28.",
  },
  {
    q: "Which Palworld materials are the biggest bottlenecks?",
    a: "Ancient Civilization Parts (boss drops only), Sulfur for Gunpowder, Pure Quartz for high-tier circuitry and Honey for Cake are the four resources that most often stall progression.",
  },
];

export const Route = createFileRoute("/items/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Items", "/items"],
      ]),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          "@id": `${URL}#dataset`,
          name: "Palworld Item and Weapon Database",
          description: DESC,
          url: URL,
          keywords: [
            "palworld items",
            "palworld weapons list",
            "palworld crafting recipes",
            "palworld materials",
            "palworld pal spheres",
          ],
          creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          isAccessibleForFree: true,
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
        }),
      },
    ],
  }),
  component: ItemsIndex,
});

function ItemsIndex() {
  const [cat, setCat] = useState<ItemCategory | "All">("All");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return ITEMS.filter(
      (i) =>
        (cat === "All" || i.category === cat) &&
        (!needle || `${i.name} ${i.category} ${i.source} ${i.stat}`.toLowerCase().includes(needle)),
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [cat, q]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        / <span className="text-foreground">Items</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Items &amp; Weapons Database</h1>
      <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
        Every Palworld item has its own page here: {ITEMS.length} weapons, ammunition types, Pal Spheres,
        armour sets, tools, base structures, consumables and materials, each listing the technology level
        that unlocks it, the exact crafting recipe or drop source, and what it is actually best used for.
      </p>
      <ContextVisualStrip variant="items" />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <label htmlFor="item-search" className="sr-only">
          Search Palworld items
        </label>
        <input
          id="item-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search item, recipe or material…"
          className="min-w-[220px] flex-1 rounded-lg glass px-4 py-2 outline-none focus:ring-2 ring-primary"
        />
        <div className="flex flex-wrap gap-2">
          {(["All", ...ITEM_CATEGORIES] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c as ItemCategory | "All")}
              aria-pressed={cat === c}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                cat === c ? "bg-primary text-primary-foreground" : "glass hover:bg-primary/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Showing <b>{rows.length}</b> of {ITEMS.length} items
      </p>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((i) => (
          <li key={i.slug} className="glass rounded-xl p-4">
            <Link
              to="/items/$slug"
              params={{ slug: i.slug }}
              className="font-semibold text-primary hover:underline"
            >
              {i.name}
            </Link>
            <div className="mt-1 text-xs text-muted-foreground">
              {i.category}
              {i.unlock > 0 ? ` · unlocks at level ${i.unlock}` : " · gathered or dropped"}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{i.stat}</p>
          </li>
        ))}
      </ul>

      {rows.length === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">
          No item matches that search. Try a material name such as “ingot” or a category filter above.
        </p>
      )}

      <FaqSection items={FAQ} heading="Palworld item database FAQ" />

      <nav aria-label="Related Palworld databases" className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Related Palworld databases</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
          <li>
            <Link to="/passives" className="font-semibold text-primary hover:underline">
              Palworld passive skills list
            </Link>
          </li>
          <li>
            <Link to="/technology-tree" className="font-semibold text-primary hover:underline">
              Palworld technology tree
            </Link>
          </li>
          <li>
            <Link to="/pals" className="font-semibold text-primary hover:underline">
              Palworld Pal database
            </Link>
          </li>
          <li>
            <Link to="/breeding" className="font-semibold text-primary hover:underline">
              Palworld breeding calculator
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
}
